import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import type { MapGeoJSONFeature } from "maplibre-gl";
import { usePriceRangesDataContext } from "@/store/PriceRangesProvider";
import {
  copyCarPriceEdaToVelo,
  createEditorZone,
  deleteEditorZone,
  updateEditorZoneHexagons,
  updateHexagonsPriceEdaBatch,
} from "@/api/admin";
import {
  applyLocalEditorZoneToSelection,
  applyLocalPriceToSelection,
  getEffectivePriceRangesMap,
  getPendingColorMap,
  getPendingPayload,
  getPriceField,
  getPriceRangesMap,
  getSelectionDraftPrice,
  mergeFeatureHexgroups,
  mergePendingUpdates,
  mergeSelections,
  removeSelection,
} from "./helpers";
import type {
  EdaEditorTarget,
  EditorMode,
  PendingColorMap,
  PendingPriceMap,
  Point,
  PriceRangeMap,
} from "./types";

type AdminPriceEdaContextValue = {
  selection: {
    items: MapGeoJSONFeature[];
    selectedIds: number[];
    selectedHexgroupId: number | null;
    selectedZoneId: number | null;
    onSelect: (
      selection: MapGeoJSONFeature[],
      point: Point,
      options?: { append?: boolean; remove?: boolean }
    ) => void;
  };
  visuals: {
    pendingColors: PendingColorMap;
    priceRangesMap: PriceRangeMap;
    priceField: string;
    refreshToken: number;
  };
  interaction: {
    editorMode: EditorMode;
    panMode: boolean;
    removeMode: boolean;
    setEditorMode: (mode: EditorMode) => void;
  };
  editor: {
    edaTarget: EdaEditorTarget;
    setEdaTarget: (target: EdaEditorTarget) => void;
    draftPrice: string;
    setDraftPrice: (value: string) => void;
    popupOpen: boolean;
    closePopup: () => void;
    error: string;
    saving: boolean;
    zoneSaving: boolean;
    copySaving: boolean;
    pendingCount: number;
    applyLocalPrice: () => void;
    confirmPending: () => Promise<void>;
    copyCarToVelo: () => Promise<void>;
    createZone: () => Promise<void>;
    updateZone: () => Promise<void>;
    deleteZone: () => Promise<void>;
  };
};

const AdminPriceEdaContext = createContext<
  AdminPriceEdaContextValue | undefined
>(undefined);

export const AdminPriceEdaProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const [selection, setSelection] = useState<MapGeoJSONFeature[]>([]);
  const [editorMode, setEditorMode] = useState<EditorMode>("click");
  const [edaTarget, setEdaTarget] = useState<EdaEditorTarget>("eda_car");
  const [draftPrice, setDraftPrice] = useState("");
  const [popupOpen, setPopupOpen] = useState(false);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const [zoneSaving, setZoneSaving] = useState(false);
  const [copySaving, setCopySaving] = useState(false);
  const [pendingUpdates, setPendingUpdates] = useState<PendingPriceMap>({});
  const [featureHexgroupMap, setFeatureHexgroupMap] = useState<
    Record<number, number>
  >({});
  const [refreshToken, setRefreshToken] = useState(0);
  const [pressedKeys, setPressedKeys] = useState({
    alt: false,
    space: false,
    z: false,
  });
  const { priceRanges, reloadPriceRanges } = usePriceRangesDataContext();

  const selectedIds = useMemo(
    () =>
      selection
        .map((feature) => Number(feature.properties.id))
        .filter(Number.isFinite),
    [selection]
  );
  const selectedHexgroupId = useMemo(() => {
    const ids = Array.from(
      new Set(
        selection
          .map((feature) => Number(feature.properties.hexgroup_id))
          .filter(Number.isFinite)
      )
    );
    return ids.length === 1 ? ids[0] : null;
  }, [selection]);
  const selectedZoneId = useMemo(() => {
    const ids = Array.from(
      new Set(
        selection
          .map((feature) => Number(feature.properties.editor_zone_id))
          .filter(Number.isFinite)
      )
    );
    return ids.length === 1 ? ids[0] : null;
  }, [selection]);
  const priceField = useMemo(() => getPriceField(edaTarget), [edaTarget]);
  const basePriceRangesMap = useMemo(
    () => getPriceRangesMap(priceRanges, edaTarget),
    [edaTarget, priceRanges]
  );
  const priceRangesMap = useMemo(
    () =>
      getEffectivePriceRangesMap(
        basePriceRangesMap,
        pendingUpdates,
        featureHexgroupMap
      ),
    [basePriceRangesMap, pendingUpdates, featureHexgroupMap]
  );
  const pendingColors = useMemo(
    () =>
      getPendingColorMap(pendingUpdates, featureHexgroupMap, priceRangesMap),
    [pendingUpdates, featureHexgroupMap, priceRangesMap]
  );
  const pendingPayload = useMemo(() => getPendingPayload(pendingUpdates), [
    pendingUpdates,
  ]);

  useEffect(() => {
    setPendingUpdates({});
    setDraftPrice(getSelectionDraftPrice(selection, {}, priceField));
  }, [priceField]);

  useEffect(() => {
    const updateKeys = (event: KeyboardEvent) => {
      setPressedKeys((prev) => ({
        alt: event.altKey,
        space: event.code === "Space" ? event.type === "keydown" : prev.space,
        z: event.key.toLowerCase() === "z" ? event.type === "keydown" : prev.z,
      }));
    };

    const resetKeys = () =>
      setPressedKeys({ alt: false, space: false, z: false });

    window.addEventListener("keydown", updateKeys);
    window.addEventListener("keyup", updateKeys);
    window.addEventListener("blur", resetKeys);

    return () => {
      window.removeEventListener("keydown", updateKeys);
      window.removeEventListener("keyup", updateKeys);
      window.removeEventListener("blur", resetKeys);
    };
  }, []);

  const onSelect: AdminPriceEdaContextValue["selection"]["onSelect"] = (
    nextSelection,
    _point,
    options
  ) => {
    const resolvedSelection = (() => {
      if (options?.remove) {
        return removeSelection(selection, nextSelection);
      }

      if (options?.append) {
        return mergeSelections(selection, nextSelection);
      }

      return nextSelection;
    })();

    setSelection(resolvedSelection);
    setFeatureHexgroupMap((prev) =>
      mergeFeatureHexgroups(prev, resolvedSelection)
    );
    setDraftPrice(
      getSelectionDraftPrice(resolvedSelection, pendingUpdates, priceField)
    );
    setError("");
    setPopupOpen(resolvedSelection.length > 0);
  };

  const closePopup = () => {
    if (saving) return;
    setPopupOpen(false);
  };

  const applyLocalPrice = () => {
    const nextPrice = Number(draftPrice);

    if (!selectedIds.length) {
      setError("Гексагоны не выбраны.");
      return;
    }

    if (!Number.isFinite(nextPrice) || nextPrice < 0) {
      setError("Введите корректное неотрицательное число.");
      return;
    }

    setError("");
    setPendingUpdates((prev) =>
      mergePendingUpdates(prev, selectedIds, nextPrice)
    );
    setSelection((prev) =>
      applyLocalPriceToSelection(prev, nextPrice, priceField)
    );
    setPopupOpen(false);
  };

  const confirmPending = async () => {
    if (!pendingPayload.length) return;

    try {
      setSaving(true);
      setError("");
      await updateHexagonsPriceEdaBatch(
        edaTarget === "eda_car" ? "car" : "velo",
        pendingPayload
      );
      await reloadPriceRanges(true);
      setRefreshToken((prev) => prev + 1);
      setPendingUpdates({});
    } catch (nextError) {
      console.error(nextError);
      setError("Не удалось подтвердить локальные изменения.");
    } finally {
      setSaving(false);
    }
  };

  const createZone = async () => {
    if (!selectedIds.length || selectedHexgroupId === null) return;

    try {
      setZoneSaving(true);
      setError("");
      const zone = await createEditorZone({
        hexgroup_id: selectedHexgroupId,
        hexagon_ids: selectedIds,
      });
      setSelection((prev) => applyLocalEditorZoneToSelection(prev, zone.id));
      setRefreshToken((prev) => prev + 1);
    } catch (nextError) {
      console.error(nextError);
      setError("Не удалось создать зону.");
    } finally {
      setZoneSaving(false);
    }
  };

  const copyCarToVelo = async () => {
    try {
      setCopySaving(true);
      setError("");
      await copyCarPriceEdaToVelo();
      await reloadPriceRanges(true);
      setRefreshToken((prev) => prev + 1);
      setPendingUpdates({});
    } catch (nextError) {
      console.error(nextError);
      setError("Не удалось скопировать EDA цену из авто в вело.");
    } finally {
      setCopySaving(false);
    }
  };

  const updateZone = async () => {
    if (!selectedIds.length || selectedZoneId === null) return;

    try {
      setZoneSaving(true);
      setError("");
      await updateEditorZoneHexagons(selectedZoneId, selectedIds);
      setSelection((prev) =>
        applyLocalEditorZoneToSelection(prev, selectedZoneId)
      );
      setRefreshToken((prev) => prev + 1);
    } catch (nextError) {
      console.error(nextError);
      setError("Не удалось обновить зону.");
    } finally {
      setZoneSaving(false);
    }
  };

  const deleteZone = async () => {
    if (selectedZoneId === null) return;

    try {
      setZoneSaving(true);
      setError("");
      await deleteEditorZone(selectedZoneId);
      setSelection((prev) => applyLocalEditorZoneToSelection(prev, null));
      setRefreshToken((prev) => prev + 1);
    } catch (nextError) {
      console.error(nextError);
      setError("Не удалось удалить зону.");
    } finally {
      setZoneSaving(false);
    }
  };

  const value = useMemo<AdminPriceEdaContextValue>(
    () => ({
      selection: {
        items: selection,
        selectedIds,
        selectedHexgroupId,
        selectedZoneId,
        onSelect,
      },
      visuals: {
        pendingColors,
        priceRangesMap,
        priceField,
        refreshToken,
      },
      interaction: {
        editorMode,
        panMode: pressedKeys.space,
        removeMode: pressedKeys.z,
        setEditorMode,
      },
      editor: {
        edaTarget,
        setEdaTarget,
        draftPrice,
        setDraftPrice,
        popupOpen,
        closePopup,
        error,
        saving,
        zoneSaving,
        copySaving,
        pendingCount: pendingPayload.length,
        applyLocalPrice,
        confirmPending,
        copyCarToVelo,
        createZone,
        updateZone,
        deleteZone,
      },
    }),
    [
      selection,
      selectedIds,
      selectedHexgroupId,
      selectedZoneId,
      pendingColors,
      priceRangesMap,
      priceField,
      refreshToken,
      editorMode,
      pressedKeys.space,
      pressedKeys.z,
      edaTarget,
      draftPrice,
      popupOpen,
      error,
      saving,
      zoneSaving,
      copySaving,
      pendingPayload.length,
    ]
  );

  return (
    <AdminPriceEdaContext.Provider value={value}>
      {children}
    </AdminPriceEdaContext.Provider>
  );
};

export const useAdminPriceEdaContext = () => {
  const context = useContext(AdminPriceEdaContext);
  if (!context) {
    throw new Error(
      "useAdminPriceEdaContext must be used within AdminPriceEdaProvider"
    );
  }
  return context;
};
